import { useTranslation } from 'react-i18next'

import { StatusPill } from '@/components/ui/StatusPill'
import { formatMoneyString } from '@/lib/format'
import type { Receivable, ReceivableStatus } from '@/types/domain'

/* Where one receivable stands, as the server judged it at cents (S-21) —
 * «Ожидается», «Частично», «Оплачен», «Переплата 12.00 USD» — written once for
 * «Счета клиенту»'s documents and the payment schedule, so the two cannot word
 * one document two ways.
 *
 * The tones are `DOC_STATUS_TONES`' for the same states: nothing paid is
 * neutral, part of it attention, all of it positive — never `brand`, which is
 * identity and the in-progress state only. An overpayment is attention, with
 * the amount above the total in words beside it, so colour never carries it
 * alone. */
const TONES: Record<ReceivableStatus, 'neutral' | 'attention' | 'positive'> = {
  open: 'neutral',
  partial: 'attention',
  paid: 'positive',
  overpaid: 'attention',
}

export function ReceivableStatusPill({ row }: { row: Receivable }) {
  const { t } = useTranslation()
  return (
    <StatusPill tone={TONES[row.status]}>
      {row.status === 'overpaid'
        ? t('finance.receivables.status.overpaid', {
            /* The remainder is served SIGNED — below zero on an overpaid row —
               and the pill states the excess unsigned: string surgery on the
               served decimal, never arithmetic (AD-9). */
            amount: formatMoneyString(
              row.outstanding.amount.replace(/^-/, ''),
              row.outstanding.currency,
            ),
          })
        : t(`finance.receivables.status.${row.status}`)}
    </StatusPill>
  )
}
